<script lang="ts">
  import { onMount } from 'svelte';
  import { useNavigate } from 'svelte-navigator';
  import { AppState, SetIconsBackground } from '../../etc/stores';
  import {
    formatMoney,
    getReferralProgramType,
    isMobile
  } from '../../etc/utils';
  import { _ } from 'svelte-i18n';

  import { Container } from '../../services/container';
  import { Auth } from '../../services/auth';
  import Analytics from '../../services/analytics';

  import Page from '../misc/Page.svelte';
  import Image from '../misc/Image.svelte';
  import Carousel from '../misc/Carousel.svelte';
  import CarouselCard from '../misc/CarouselCard.svelte';
  import getCarouselCards from '../../pages/Welcome/cards';
  import PoweredBy from '../misc/PoweredBy.svelte';
  import CloseV2 from '../icons/CloseV2.svelte';
  import flags from '../../etc/flagKeys';
  import { replaceVariables } from '../../i18n';
  import AuthConnector from '../misc/AuthConnector.svelte';
  import {
    AUTH_STORAGE_FLAGS,
    SessionManager
  } from '../../services/session-manager';

  const navigate = useNavigate();

  const auth = Container.get(Auth);
  const session = Container.get(SessionManager) as SessionManager;

  let clicked_join = false;
  let guest = $AppState.website.flowGuestSignIn;
  let category = 'guest-state';

  let handleConnectCustomer;

  const isBlack = $AppState.theme.name === 'black';
  const isBorder = !$AppState.business.coinSetup;

  const t = (text: string, values: any = {}) => {
    return replaceVariables($_(`welcome.${text}`), values);
  };

  const tCards = (text: string) => {
    return t(`cards.${text}`, {
      LOYALTY_PERCENTAGE: Number(
        $AppState.business.rewardsSettings.coinRewardPercentage
      ),
      MONEY: `${formatMoney($AppState.business.location, 1)} ${
        $AppState.business.location.currency
      }`
    });
  };

  let carouselCards = [];
  $: {
    const referralActive = !!$AppState.referralProgram?.isActive;
    const socialRewardActive =
      $AppState.socialReward.length > 0 &&
      $AppState.featureFlags.includes(flags.SOCIAL_REWARDS);

    const type = getReferralProgramType($AppState.referralProgram);

    carouselCards = getCarouselCards(tCards, {
      isEnabledSocial: socialRewardActive,
      isEnabledReferral: referralActive,
      defaultTextReferral: referralActive && type === 'only-referred'
    }).filter(item => !item.disabled);
  }

  const logo = $AppState.business.logo?.publicPath.large;

  const isReferredState = !!$AppState.referralReferredState.id;
  const hasMessage = isReferredState || $AppState.freeCoinsCampaign.id;

  const onJoin = async (signIn?: boolean) => {
    onGAEventClickJoin(signIn);

    if (isReferredState) {
      AppState.update(state => ({
        ...state,
        website: {
          ...state.website,
          authFlow: 'referred-redeem'
        }
      }));
      if ($AppState.referralReferredState.businessReferral?.isActive)
        session.set(AUTH_STORAGE_FLAGS.referral, location.search);
    }

    if (await auth.isAuthenticatedOnWebsite()) {
      handleConnectCustomer();
    } else if (auth.canOpenLoginModal()) {
      auth.openLoginModal({ mode: signIn ? 'login' : 'signup' });
    }
  };

  const onBeforeConnectCustomer = async () => {
    if (!$AppState.auth?.providerData?.email && $AppState.auth.connected) {
      await auth.loadState();
    }
  };

  const onAfterConnectCustomer = async () => {
    onGAEventJoinSuccess();
    onChangeShowFooter(true);
  };

  $: category = guest ? 'guest-state' : 'join-state';

  const onGAEventJoinSuccess = () => {
    if (clicked_join) Analytics.logEvent('click-join-success', category);
    clicked_join = false;
  };

  const onGAEventClickJoin = (signIn = false) => {
    if (!clicked_join)
      Analytics.logEvent('click-join', category, ['segmentio'], {
        SignIn: signIn
      });
    clicked_join = true;
  };

  const onSignIn = () => {
    onJoin(true);
  };

  const getPaddingWelcome = () => {
    let paddingTop = '24px';
    if (!hasMessage) paddingTop = '32px';

    if (isMobile()) {
      if (window.innerHeight >= 660) {
        paddingTop = '43px';
        if (!hasMessage) paddingTop = '59px';
      }
      return `${paddingTop} 32px 0`;
    }
    return `${paddingTop} 32px 0`;
  };

  const onChangeShowFooter = show => {
    AppState.update(state => ({
      ...state,
      website: {
        ...state.website,
        footerVisibility: show
      }
    }));
  };

  const triggerConnectCustomer = () => {
    onGAEventClickJoin();

    handleConnectCustomer();
  };

  onMount(() => {
    if (
      $AppState.website.flow &&
      $AppState.auth.connected &&
      !$AppState.website.authFlow
    ) {
      navigate($AppState.website.flow);
    }
    Analytics.logPageView('Welcome', '/welcome');

    SetIconsBackground('themed');

    onChangeShowFooter(false);

    setTimeout(() => {
      document.activeElement?.blur();
    }, 0);

    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 700);
  });
</script>

{#if $AppState.business}
  <AuthConnector
    {onAfterConnectCustomer}
    {onBeforeConnectCustomer}
    {triggerConnectCustomer}
    loggerContext="Welcome.svelte"
    let:loading
    bind:handleConnectCustomer
  >
    <div class="welcome">
      <div class="welcome__background">
        <div class="welcome__background-light" />
      </div>
      <Page
        hideCloseButton={!isMobile()}
        closeIcon={CloseV2}
        className="welcome__container"
        padding={getPaddingWelcome()}
      >
        <div class="welcome__coin" class:--border={isBorder}>
          {#if logo}
            <div class="welcome__coin__logo" class:--border={isBorder}>
              <Image src={logo} round={true} />
            </div>
          {:else}
            <Image src={`${process.env.BASE_URL}/images/welcome-coin.svg`} />
          {/if}
        </div>
        <slot {onJoin} {onSignIn} {loading} />
      </Page>
      <Carousel>
        {#each carouselCards as { description, title, icon, widthIcon, heightIcon }}
          <CarouselCard>
            <div slot="icon" style="height: {heightIcon || '22px'}">
              <i
                class="welcome__carousel-icon"
                style={`background-image: url('${
                  process.env.BASE_URL
                }/images/emojis/${icon}.png'); width: ${
                  widthIcon || '22px'
                }; height: ${heightIcon || '22px'}`}
              />
            </div>
            <div slot="title">{title}</div>
            <div slot="description">{@html description}</div>
          </CarouselCard>
        {/each}
      </Carousel>
      <PoweredBy
        classContainer="welcome__footer-active"
        color={isBlack ? '#FFF' : $AppState.theme.colors.typography}
      />
    </div>
  </AuthConnector>
{/if}

<style lang="scss">
  :global(.welcome__text-free-coins) {
    margin-left: 4px !important;
    font-weight: 400 !important;
    @media screen and (max-width: 468px) and (min-height: 700px) {
      font-size: 16px !important;
      line-height: 16px !important;
    }
  }

  :global(.welcome_button-join) {
    box-shadow: none !important;
  }

  :global(.twism-text-button) {
    font-size: 14px !important;
  }

  :global(.twism-button-input-header) {
    position: absolute !important;
    height: 40px !important;
    width: 66px !important;
    right: 4px;
    top: 4px;
  }

  :global(.welcome__footer-active) {
    opacity: 0.7;
    display: flex !important;
    align-items: flex-end;
    justify-content: center;
    margin-top: auto !important;
    margin-bottom: 16px !important;
    width: 100%;
  }

  :global(.welcome__footer-active > svg) {
    bottom: 0 !important;
    margin-bottom: 0 !important;
  }

  :global(.sc-carousel__carousel-container) {
    margin-bottom: 43px !important;
  }

  .welcome {
    height: 100%;
    background-color: var(--twism-theme-welcome-backgroundLight);
    display: flex;
    flex-direction: column;
    position: relative;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;

    &::-webkit-scrollbar {
      display: none;
    }

    :global(.welcome__container) {
      height: auto !important;
      z-index: 2;
      position: relative;
    }

    &__coin {
      width: 80px;
      height: 80px;
      display: flex;
      justify-content: center;
      pointer-events: none;
      user-select: none;

      &.--border {
        box-sizing: content-box;
        border: 3px solid #92a2b0;
        border-radius: 50%;
      }
      &__logo {
        border-radius: 50%;
        &.--border {
          box-sizing: content-box;
          border: 1px solid #fff;
        }
      }
    }

    &__background {
      background-color: var(--twism-theme-welcome-background);
      position: absolute;
      top: 0;
      height: 417px;
      width: 100%;
      @media screen and (max-width: 468px) and (min-height: 700px) {
        height: 496px;
      }
    }
    &__background-light {
      position: absolute;
      width: 221px;
      height: 80.95px;
      left: 69px;
      top: -49.48px;

      background: var(--twism-theme-welcome-colorLight);
      filter: blur(71px);
    }

    &__carousel-icon {
      height: 22px;
      width: 22px;
      display: inline-block;
      background-size: 100%;
      background-repeat: no-repeat;
    }
  }
</style>
